import React, {useState, useEffect} from 'react'
import {FormikProps} from 'formik'
import {IntlShape} from 'react-intl'
import {TourDetails} from '../core/_models'
import {FAQ} from './GeneralTab/components/FAQ'
import {IncludeExclude} from './GeneralTab/components/IncludeExclude'
import {LocationPoint} from './GeneralTab/components/LocationPoint'
import {useMutation, useQueryClient} from 'react-query'
import {patchOne, createOne as createOneService} from '../../../../services/common_requests'
import {toast} from 'react-toastify'
import {
  buildDetailPayload,
  isContentCategoryType,
  localizeDetails,
} from './contentCategoryUtils'

interface FaqIncludesExcludesTabProps {
  formik: FormikProps<TourDetails>
  intl: IntlShape
  tourId: string
  hideFAQ?: boolean
  fetchTourDetails: () => Promise<void>
  selectedLanguage: string
  setSelectedLanguage: (lang: string) => void
}

interface CategoryItem {
  _id: string
  tour: string
  type: 'includes' | 'excludes' | 'INCLUDES' | 'EXCLUDES'
  title: string
  description: string
  details: Array<{
    title: string
    description: string
    translations?: Array<{
      language: string
      title: string
      description: string
    }>
  }>
  translations?: Array<{
    language: string
    title: string
    description: string
  }>
  createdAt?: string
  updatedAt?: string
  __v?: number
  id?: string
}

// Extend TourDetails to include contentCategories
declare module '../core/_models' {
  interface TourDetails {
    contentCategories?: CategoryItem[]
    includes?: Array<{title: string; description: string}>
    excludes?: Array<{title: string; description: string}>
  }
}

const FaqIncludesExcludesTab: React.FC<FaqIncludesExcludesTabProps> = ({
  formik,
  intl,
  tourId,
  hideFAQ = false,
  fetchTourDetails,
  selectedLanguage,
  setSelectedLanguage
}) => {
  const queryClient = useQueryClient()
  // State for forms visibility
  const [showFAQForm, setShowFAQForm] = useState(false)
  const [showIncludeForm, setShowIncludeForm] = useState(false)
  const [showExcludeForm, setShowExcludeForm] = useState(false)
  const [showEducationForm, setShowEducationForm] = useState(false)
  const [showHealthForm, setShowHealthForm] = useState(false)
  const [showTransportForm, setShowTransportForm] = useState(false)
  
  // State for new form items
  const [newFAQ, setNewFAQ] = useState({title: '', content: ''})
  const [newInclude, setNewInclude] = useState({title: '', description: ''})
  const [newExclude, setNewExclude] = useState({title: '', description: ''})
  const [newEducation, setNewEducation] = useState({name: '', content: '', distance: ''})
  const [newHealth, setNewHealth] = useState({name: '', content: '', distance: ''})
  const [newTransport, setNewTransport] = useState({name: '', content: '', distance: ''})

  // Error states
  const [includeError, setIncludeError] = useState('')
  const [excludeError, setExcludeError] = useState('')
  const [faqError, setFaqError] = useState('')

  // Get IDs from contentCategories in formik
  const includesId =
    formik.values.contentCategories?.find((item: CategoryItem) =>
      isContentCategoryType(item.type, 'includes'),
    )?._id || null
  const excludesId =
    formik.values.contentCategories?.find((item: CategoryItem) =>
      isContentCategoryType(item.type, 'excludes'),
    )?._id || null

  const reloadContentCategories = async () => {
    await queryClient.invalidateQueries([`tour-content-categories-${tourId}`])
    await queryClient.refetchQueries([`tour-content-categories-${tourId}`])
  }

  // Update mutation types
  const patchMutation = useMutation(
    ({id, ...data}: {
      id: string;
      tour: string;
      type: 'includes' | 'excludes';
      title: string;
      description: string;
      details: Array<{
        title: string;
        description: string;
        translations: Array<{
          language: string;
          title: string;
          description: string;
        }>;
      }>;
    }) =>
      patchOne(data, `/tour-content-categories/${id}`),
    {
      onSuccess: async () => {
        await reloadContentCategories()
      },
      onError: (error) => {
        toast.error('Failed to update content category. Please try again.')
      }
    }
  )

  // Create mutation for new content categories
  const createMutation = useMutation(
    (data: {
      tour: string;
      type: 'includes' | 'excludes';
      title: string;
      description: string;
      details: Array<{
        title: string;
        description: string;
        translations: Array<{
          language: string;
          title: string;
          description: string;
        }>;
      }>;
    }) =>
      createOneService(data, '/tour-content-categories'),
    {
      onSuccess: async () => {
        await reloadContentCategories()
      },
      onError: (error) => {
        toast.error('Failed to create content category. Please try again.')
      }
    }
  )

  // Sync includes/excludes from contentCategories when language or source data changes
  useEffect(() => {
    if (!formik?.setFieldValue) {
      return
    }

    const categories = formik.values.contentCategories
    if (!Array.isArray(categories)) {
      return
    }

    const includesItem = categories.find((item: CategoryItem) =>
      isContentCategoryType(item.type, 'includes'),
    )
    const excludesItem = categories.find((item: CategoryItem) =>
      isContentCategoryType(item.type, 'excludes'),
    )

    formik.setFieldValue(
      'includes',
      includesItem?.details
        ? localizeDetails(includesItem.details, selectedLanguage)
        : [],
    )

    formik.setFieldValue(
      'excludes',
      excludesItem?.details
        ? localizeDetails(excludesItem.details, selectedLanguage)
        : [],
    )
    // Serialize details so sync re-runs even if array reference is reused after parent setValues
  }, [
    formik.values.contentCategories,
    selectedLanguage,
    JSON.stringify(
      (formik.values.contentCategories || []).map((c: CategoryItem) => ({
        id: c?._id,
        type: c?.type,
        detailsLen: c?.details?.length,
        details: c?.details,
      })),
    ),
  ])

  // Validation helper
  const validateFields = (title: string, description: string): boolean => {
    if (!title.trim()) {
      return false
    }
    if (!description.trim()) {
      return false
    }
    return true
  }

  // Modify handleAddInclude
  const handleAddInclude = async () => {
    setIncludeError('')
    
    if (!validateFields(newInclude.title, newInclude.description)) {
      setIncludeError('Both title and description are required')
      return
    }

    const items = [
      ...(formik.values.includes || []),
      {title: newInclude.title.trim(), description: newInclude.description.trim()},
    ]

    // Get existing content category if it exists
    const existingCategory = formik.values.contentCategories?.find((item: CategoryItem) =>
      isContentCategoryType(item.type, 'includes'),
    )

    const requestBody = {
      tour: tourId,
      type: 'includes' as const,
      title: existingCategory?.title || newInclude.title.trim(),
      description: existingCategory?.description || newInclude.description.trim(),
      details: items.map((item, index) =>
        buildDetailPayload(
          item,
          index,
          existingCategory?.details,
          selectedLanguage,
        ),
      ),
    }

    try {
      if (includesId) {
        await patchMutation.mutateAsync({id: includesId, ...requestBody})
      } else {
        await createMutation.mutateAsync(requestBody)
      }
      formik.setFieldValue('includes', items)
      setNewInclude({title: '', description: ''})
      setShowIncludeForm(false)
    } catch (error) {
      console.error('Error adding include:', error)
    }
  }

  const handleDeleteInclude = async (index: number) => {
    if (includesId) {
      const items = (formik.values.includes || []).filter((_: any, i: number) => i !== index)
      const existingCategory = formik.values.contentCategories?.find((item: CategoryItem) =>
        isContentCategoryType(item.type, 'includes'),
      )
      const remainingExistingDetails = (existingCategory?.details || []).filter(
        (_: any, i: number) => i !== index,
      )

      const requestBody = {
        tour: tourId,
        type: 'includes' as const,
        title: existingCategory?.title || '',
        description: existingCategory?.description || '',
        details: items.map((item, detailIndex) =>
          buildDetailPayload(
            item,
            detailIndex,
            remainingExistingDetails,
            selectedLanguage,
          ),
        ),
      }
      try {
        await patchMutation.mutateAsync({id: includesId, ...requestBody})
      } catch (error) {
        console.error('Error deleting include:', error)
      }
    }
  }

  // Modify handleAddExclude
  const handleAddExclude = async () => {
    setExcludeError('')
    
    if (!validateFields(newExclude.title, newExclude.description)) {
      setExcludeError('Both title and description are required')
      return
    }

    const items = [
      ...(formik.values.excludes || []),
      {title: newExclude.title.trim(), description: newExclude.description.trim()},
    ]

    // Get existing content category if it exists
    const existingCategory = formik.values.contentCategories?.find((item: CategoryItem) =>
      isContentCategoryType(item.type, 'excludes'),
    )

    const requestBody = {
      tour: tourId,
      type: 'excludes' as const,
      title: existingCategory?.title || newExclude.title.trim(),
      description: existingCategory?.description || newExclude.description.trim(),
      details: items.map((item, index) =>
        buildDetailPayload(
          item,
          index,
          existingCategory?.details,
          selectedLanguage,
        ),
      ),
    }

    try {
      if (excludesId) {
        await patchMutation.mutateAsync({id: excludesId, ...requestBody})
      } else {
        await createMutation.mutateAsync(requestBody)
      }
      formik.setFieldValue('excludes', items)
      setNewExclude({title: '', description: ''})
      setShowExcludeForm(false)
    } catch (error) {
      console.error('Error adding exclude:', error)
    }
  }

  const handleDeleteExclude = async (index: number) => {
    if (excludesId) {
      const items = (formik.values.excludes || []).filter((_: any, i: number) => i !== index)
      const existingCategory = formik.values.contentCategories?.find((item: CategoryItem) =>
        isContentCategoryType(item.type, 'excludes'),
      )
      const remainingExistingDetails = (existingCategory?.details || []).filter(
        (_: any, i: number) => i !== index,
      )

      const requestBody = {
        tour: tourId,
        type: 'excludes' as const,
        title: existingCategory?.title || '',
        description: existingCategory?.description || '',
        details: items.map((item, detailIndex) =>
          buildDetailPayload(
            item,
            detailIndex,
            remainingExistingDetails,
            selectedLanguage,
          ),
        ),
      }
      try {
        await patchMutation.mutateAsync({id: excludesId, ...requestBody})
      } catch (error) {
        console.error('Error deleting exclude:', error)
      }
    }
  }

  // FAQ handler
  const handleAddFAQ = () => {
    setFaqError('')
    
    if (!validateFields(newFAQ.title, newFAQ.content)) {
      setFaqError('Both title and content are required')
      return
    }

    handleAddElement('faqs', {
      title: newFAQ.title.trim(),
      content: newFAQ.content.trim(),
    })
    setNewFAQ({title: '', content: ''})
    setShowFAQForm(false)
  }

  // Helper function to add elements (for FAQ, surroundings)
  const handleAddElement = (field: keyof TourDetails, element: any) => {
    const currentValue = formik.values[field] || []
    if (Array.isArray(currentValue)) {
      formik.setFieldValue(field, [...currentValue, element])
    }
  }

  return (
    <div className='d-flex flex-column gap-7 gap-lg-10'>
      {/* FAQ Section - only show if hideFAQ is false */}
      {!hideFAQ && (
        <FAQ
          formik={formik}
          showForm={showFAQForm}
          setShowForm={setShowFAQForm}
          newFAQ={newFAQ}
          setNewFAQ={setNewFAQ}
          handleAddFAQ={handleAddFAQ}
          intl={intl}
          error={faqError}
        />
      )}
      {/* Includes Section */}
      <IncludeExclude
        formik={formik}
        type='includes'
        showForm={showIncludeForm}
        setShowForm={setShowIncludeForm}
        newItem={newInclude}
        setNewItem={setNewInclude}
        handleAddItem={handleAddInclude}
        handleDeleteItem={handleDeleteInclude}
        intl={intl}
        patchMutation={patchMutation}
        contentCategoryId={includesId}
        error={includeError}
        isLoading={patchMutation.isLoading || createMutation.isLoading}
        selectedLanguage={selectedLanguage}
      />
      {/* Excludes Section */}
      <IncludeExclude
        formik={formik}
        type='excludes'
        showForm={showExcludeForm}
        setShowForm={setShowExcludeForm}
        newItem={newExclude}
        setNewItem={setNewExclude}
        handleAddItem={handleAddExclude}
        handleDeleteItem={handleDeleteExclude}
        intl={intl}
        patchMutation={patchMutation}
        contentCategoryId={excludesId}
        error={excludeError}
        isLoading={patchMutation.isLoading || createMutation.isLoading}
        selectedLanguage={selectedLanguage}
      />
      {/* Surroundings Section */}
      {/* <div className='card card-flush py-4'>
        <div className='card-header'>
          <div className='card-title'>
            <h2>{intl.formatMessage({id: 'TOUR.FIELDS.SURROUNDINGS'})}</h2>
          </div>
        </div>
        <div className='card-body pt-0'>
          <LocationPoint
            formik={formik}
            type='education'
            showForm={showEducationForm}
            setShowForm={setShowEducationForm}
            newItem={newEducation}
            setNewItem={setNewEducation}
            handleAddItem={handleAddEducation}
            intl={intl}
          />
          <LocationPoint
            formik={formik}
            type='health'
            showForm={showHealthForm}
            setShowForm={setShowHealthForm}
            newItem={newHealth}
            setNewItem={setNewHealth}
            handleAddItem={handleAddHealth}
            intl={intl}
          />
          <LocationPoint
            formik={formik}
            type='transport'
            showForm={showTransportForm}
            setShowForm={setShowTransportForm}
            newItem={newTransport}
            setNewItem={setNewTransport}
            handleAddItem={handleAddTransport}
            intl={intl}
          />
        </div>
      </div> */}
      
      {/* Remove global save/cancel buttons - each item has its own save functionality */}
    </div>
  )
}

export {FaqIncludesExcludesTab}